<template>
  <PageStyle contentClass="bg-transparent border-0">
    <template #pageHeader>
      <router-link
        :to="{
          name: 'applications/landing',
          params: { applicationId: applicationId },
        }"
        class="inline-flex items-center text-sm text-gray-700 hover:text-gray-700 hover:underline"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="feather feather-chevron-left w-4 h-4"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        <span class="ml-1">Back</span>
      </router-link>
    </template>
    <template #pageContent>
      <div>
        <h1 class="font-montserrat text-2xl font-bold text-university-primary">
          Participant Agreement
        </h1>
        <div v-if="detailsLoading">
          <spinner>
            <template #spinnercontent>
              <p class="text-gray-600">
                Loading Participant Agreement
              </p>
            </template>
          </spinner>
        </div>
        <mobile-app-form-completion v-else-if="showPromotion" formLabel="Participant Agreement" />
        <div
          v-else
          id="participant-agreement-insurance-direct"
          role="form"
          aria-label="Custom Participant Agreement Form"
          class="mt-10 relative"
        />
      </div>
    </template>
  </PageStyle>
</template>

<script>
import tailwind from "@apiabroad/formio-tailwind-template";
import { datadogLogs } from "@datadog/browser-logs";
import { Formio } from "formiojs";
import { mapState } from "vuex";
import MobileAppFormCompletion from "@/components/forms/MobileAppFormCompletion.vue";
import PageStyle from "@/components/forms/SharedComponents/Layout/PageDefault.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import formIoApi from "@/mixins/formIoApi.js";
import forms from "@/mixins/forms.js";
import impersonationFormSniffer from "@/mixins/ImpersonationFormSniffer";
import formService from "@/services/form";
import ordersService from "@/services/orders";

export default {
  name: "ParticipantAgreementForm",
  components: {
    PageStyle,
    MobileAppFormCompletion,
    Spinner,
  },
  mixins: [formIoApi, forms, impersonationFormSniffer],
  data() {
    return {
      submissionId: "",
      formURL: "cpparticipantagreementinsurancedirectpay",
      experienceDetails: null,
      detailsLoading: true,
    };
  },
  computed: {
    ...mapState(["featureFlags", "currentUser"]),
    showPromotion() {
      return Boolean(
        this.experienceDetails &&
          this.experienceDetails.show_mobile_app_promotions &&
          this.featureFlags["force-mobile-form-adoption"],
      );
    },
    applicationId() {
      return this.$route.params.applicationId;
    },
  },
  async created() {
    const valid = await this.isValidApplication(
      this.applicationId,
      "CP Participant Agreement Form - Insurance & Direct Pay",
      "post_acceptance_form_rules",
    );

    if (!valid) {
      this.$router.push({ name: "experiences" });
      return;
    }

    this.getFormioData();

    try {
      const experienceSummary = await ordersService.getMyExperiencesSummary(this.currentUser?.participantId);
      this.experienceDetails = experienceSummary.find((s) => s.application_id === this.applicationId);
    } catch (err) {
      datadogLogs.logger.error(
        "Error loading experience details",
        {
          applicationId: this.applicationId,
        },
        err,
      );
    }

    this.detailsLoading = false;
  },
  methods: {
    getFormioData() {
      this.formioSubmissionExists(this.formURL, this.applicationId).then((submissionId) => {
        this.submissionId = submissionId;
        this.createPDF();
      });
    },
    createPDF() {
      const element = document.getElementById("participant-agreement-insurance-direct");
      let url = `/api/forms/${this.formURL}`;
      if (this.submissionId) {
        url += "/submission/" + this.submissionId;
      }

      Formio.setBaseUrl(`${window.location.protocol}//${window.location.host}`);
      Formio.setAuthUrl(`${window.location.protocol}//${window.location.host}/api/forms`);
      Formio.use(tailwind);
      Formio.createForm(element, url, {
        hooks: {
          beforeSubmit: (submission, next) => {
            submission.data.application_id = this.applicationId;
            submission = this.attachEmployeeToSubmission(submission, "post");
            next();
          },
        },
        noAlerts: true,
      }).then((form) => {
        form.nosubmit = true;
        form.on("submit", (submission) => {
          formService.createOrUpdateSubmission(this.formURL, submission, this.submissionId).then((submission) => {
            form.emit("submitDone", submission);
          });
        });
        form.on("submitDone", (submission) => {
          this.logSubmission(this.formURL, {}, submission.data);
          this.$router.push({
            name: "applications/landing",
            params: {
              applicationId: this.applicationId,
            },
          });
        });
      });
    },
  },
};
</script>
